import type { FC } from 'react';
import { useContext, useMemo } from 'react';
import c from 'classnames';
import { useSelector } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import Slider from '@wix/da-ds/pkg/Slider';
import ProfileFolderCard from '../ProfileFolderCard';
import AddNewFolderCard from '../AddNewFolderCard';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { filterOutEmpty } from '../../../../utils/gallection';
import { useGallectionFolders } from '../hooks/useGallectionFolders';
import { getCanCreateGalleryFolder } from '../../../../selectors/privs';
import { PapiGallection } from '@wix/da-papi-types';
import {
  getProfileOwnerUser,
  isCurrentUserProfileOwner,
} from '../../../../selectors/users';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

import s from './FolderSlider.scss';

export interface Props {
  className?: string;
  cardWidth?: number;
  folder?: PapiGallection | null;
  subfolder?: PapiGallection | null;
  unsetSelectedSubfolderId: () => void;
}

const FolderSlider: FC<Props> = ({
  className,
  cardWidth: cardWidthFromProps,
  folder,
  subfolder,
  unsetSelectedSubfolderId,
}) => {
  const canCreateFolder = useSelector(getCanCreateGalleryFolder);
  const isProfileOwner = useSelector(isCurrentUserProfileOwner);
  const profileOwnerUser = useSelector(getProfileOwnerUser);

  const {
    hasMore,
    hasLess,
    fetchNext,
    fetchPrevious,
    isFetching,
    folders,
    selectedFolderId,
  } = useGallectionFolders();

  const foldersToRender = useMemo(() => {
    return isProfileOwner ? folders : filterOutEmpty(folders);
  }, [isProfileOwner, folders]);

  const isMobile = useContext(MobileContext);
  const cardWidth = useMemo(() => {
    return cardWidthFromProps
      ? cardWidthFromProps
      : parseInt(s['folder-card-width']);
  }, [cardWidthFromProps]);

  const scrollToIndex = foldersToRender.findIndex(({ folderId }) => {
    return folderId === selectedFolderId;
  });

  return (
    <Slider
      className={c(s['folders-slider'], isMobile && s['mobile'], className)}
      scrollToIndex={scrollToIndex}
      smoothInitialScroll={false}
    >
      {hasLess && (
        <Waypoint onEnter={fetchPrevious} horizontal topOffset="-20%" />
      )}
      {foldersToRender.map((f, index) => {
        const onClick: (() => void) | null = null;
        let isSelected = false;
        const isParent: boolean =
          f.folderId === folder?.folderId && folder?.hasSubfolders;

        if (folder && f.folderId === folder.folderId) {
          isSelected = true;
        }

        return (
          <ProfileFolderCard
            isSelected={isSelected}
            withPrice
            width={cardWidth}
            className={s['folder-card']}
            key={f.folderId}
            folder={f}
            indexInsideParent={index}
            withLink
            isProfileOwner={isProfileOwner}
            profileOwnerUser={profileOwnerUser}
            onClick={onClick}
            isParent={isParent}
          />
        );
      })}
      {isFetching && <LoadingIndicator className={s['folder-card']} />}
      {hasMore && (
        <Waypoint onEnter={fetchNext} horizontal bottomOffset="-20%" />
      )}
      {canCreateFolder && (
        <AddNewFolderCard className={s['folder-card']} width={cardWidth} />
      )}
    </Slider>
  );
};

FolderSlider.displayName = 'FolderSlider';
export default FolderSlider;
